import React from 'react';
import { Avatar, Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HeaderActions from './HeaderActions';
import useInterveneSx from '../../hooks/useInterveneSx';
import useInterveneTheme from '../../hooks/useInterveneTheme';
import { useInterveneSocketContext } from '../../sockets';
import {
  getConversationAvatarInitial,
  getConversationDisplayName,
  getConversationPhone,
} from '../../utils/conversationIdentity';
import {
  formatLastSeenLabel,
  formatPhoneSuffix,
} from '../../utils/chatHeaderIdentity';
import { getConversationAvatarTone } from '../../utils/getConversationAvatarTone';
import { isComposerActive } from '../../utils/composerMode';
import { INTERVENE_AUTOMATION_COPY } from '../../constants/interveneAutomationCopy';

function HeaderStatusLine({ label, tooltip, styles }) {
  return (
    <Box sx={styles.sessionStatusRow}>
      <Typography sx={styles.sessionStatusText} noWrap component="span">
        {label}
      </Typography>
      {tooltip ? (
        <Tooltip title={tooltip} arrow placement="bottom">
          <IconButton size="small" sx={styles.sessionInfoButton} aria-label="Session info">
            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
}

export default function Header({
  activeUser,
  messages = [],
  composerMode = 'idle',
  showSessionToggle,
  canStartSession,
  canEndSession,
  onStartSession,
  onEndSession,
  onToggleProfile,
  isProfileOpen = false,
  onCall,
  onBackToList,
  isHandledByOther = false,
  handledByName = '',
}) {
  const styles = useInterveneSx().chatHeader;
  const { chat } = useInterveneTheme();
  const { viewers = [] } = useInterveneSocketContext();
  const userName = getConversationDisplayName(activeUser, 'User');
  const userPhone = getConversationPhone(activeUser);
  const phoneSuffix = formatPhoneSuffix(userPhone);
  const lastSeenLabel = formatLastSeenLabel(activeUser, messages);
  const userInitial = getConversationAvatarInitial(activeUser);
  const avatarTone = getConversationAvatarTone(chat, { selected: true });
  const isLiveSession = isComposerActive(composerMode) && canEndSession;

  let secondaryLine = lastSeenLabel ? (
    <Typography sx={styles.lastSeenText} noWrap>
      {lastSeenLabel}
    </Typography>
  ) : null;

  if (isHandledByOther) {
    const handlerLabel = handledByName || activeUser?.intervened_by || 'another agent';
    secondaryLine = (
      <HeaderStatusLine
        styles={styles}
        label={`Handled by ${handlerLabel} · Bot paused`}
        tooltip={`${handlerLabel} is handling this chat. Bot flows and customer replies are paused.`}
      />
    );
  } else if (isLiveSession) {
    secondaryLine = (
      <HeaderStatusLine
        styles={styles}
        label={INTERVENE_AUTOMATION_COPY.header.liveSessionStatus}
        tooltip={INTERVENE_AUTOMATION_COPY.header.liveSessionTooltip}
      />
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.leadingCluster}>
        {onBackToList ? (
          <Tooltip title="Back to conversations">
            <IconButton
              onClick={onBackToList}
              aria-label="Back to conversations"
              sx={styles.backButton}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}

        <Tooltip title="View contact details">
          <Box
            component="button"
            type="button"
            onClick={onToggleProfile}
            aria-label={`View ${userName} contact details`}
            aria-expanded={isProfileOpen}
            sx={[
              styles.profileTrigger,
              isProfileOpen && styles.profileTriggerActive,
            ]}
          >
            <Avatar
              src={activeUser.profile_pic || ''}
              sx={{
                ...styles.avatar,
                bgcolor: avatarTone.background,
                color: avatarTone.foreground,
              }}
            >
              {userInitial}
            </Avatar>

            <Box sx={styles.userInfo}>
              <Box sx={styles.nameRow}>
                <Typography sx={styles.userName} noWrap>
                  {userName}
                </Typography>
                {phoneSuffix ? (
                  <Typography sx={styles.phoneSuffix} noWrap>
                    {phoneSuffix}
                  </Typography>
                ) : null}
                {viewers.length > 0 && (
                  <Chip
                    size="small"
                    label={`${viewers.length} viewing`}
                    title={viewers.join(', ')}
                    sx={styles.viewingChip}
                  />
                )}
              </Box>
              {secondaryLine}
            </Box>
          </Box>
        </Tooltip>
      </Box>

      <HeaderActions
        showSessionToggle={showSessionToggle}
        canStartSession={canStartSession}
        canEndSession={canEndSession}
        onStartSession={onStartSession}
        onEndSession={onEndSession}
        onCall={onCall}
      />
    </Box>
  );
}
